import { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { InlineNotification } from '@carbon/react';
import type { CompletionConfigurationResponse } from 'types/api-responses';
import { createFileAtom } from 'lib/atoms/file-atom';
import { useConfigurationOptionsAtom } from './atoms';
import {
  getPromptsForDocumentType,
  extensionsFromMimeTypes,
  DEFAULT_ACCEPTED_FORMATS,
} from './utils';
import { useProvisionFieldSelection } from './use-provision-field-selection';
import { ConfigureProvisionFields } from './provision-fields';
import { DocumentUploadField } from './document-upload-field';

// The upload half of provisioning, with no opinion about what the document gets attached to:
// a single-file DirectUpload atom plus the OCR/LLM/prompt selection, validated against the
// backend's configuration list.
//
// Callers own the POST. That is the whole point of the split — Related Items files the
// document against a host record, Vesting Documents against a CRM tract, and the two endpoints
// take different payload wrappers. Mirrors Document::ProvisionFromUpload on the backend, which
// owns provisioning while each caller owns what it links.

/** What a completed form produces. The caller wraps this for its own endpoint. */
export interface ProvisionPayload {
  fileSignedId: string;
  documentExtraction: { configurationId: number };
  documentExtractionCompletion: { configurationId: number; promptId: number };
}

/** The caller drives submit through this handle, because it owns the Save button. */
export interface ProvisionUploadFormHandle {
  submit: () => void;
}

interface Props {
  /**
   * The DocumentCreator selector the backend provisions as — 'courthouse', 'afe', … NOT the STI
   * class name and NOT the recording vocabulary. Undefined means the caller has no type yet,
   * which reads the same as one the backend does not list: unprovisionable.
   */
  documentType?: string;
  /** Namespaces the field ids, so two forms can share a page. */
  idPrefix: string;
  /** Report whether Save may fire (file uploaded + config chosen). */
  onReadyChange: (ready: boolean) => void;
  /** A completed form. The caller POSTs it and owns what happens next. */
  onSubmit: (payload: ProvisionPayload) => void;
  /** Caller-owned in-flight state, so the fields lock while its request is open. */
  submitting?: boolean;
  /** Caller-owned failure text, rendered above the fields. */
  error?: string | null;
  onDismissError?: () => void;
}

export const ProvisionUploadForm = forwardRef<ProvisionUploadFormHandle, Props>(
  (
    {
      documentType,
      idPrefix,
      onReadyChange,
      onSubmit,
      submitting = false,
      error = null,
      onDismissError,
    },
    ref
  ) => {
    const fileAtom = useMemo(() => createFileAtom(), []);
    const [upload, setUpload] = useAtom(fileAtom);

    const configAtom = useConfigurationOptionsAtom('/configurations');
    const configState = useAtomValue(configAtom);
    const configObject = configState.data;
    const config =
      configObject && !('error' in configObject)
        ? (configObject as CompletionConfigurationResponse)
        : undefined;
    const configErrored = !!configObject && 'error' in configObject;

    const typeOption = config?.documentTypes.find(
      (dt) => dt.value === documentType
    );
    // Accept exactly what this document type provisions, same as the Add Document modal.
    const accept = extensionsFromMimeTypes(
      typeOption?.acceptedFormats ?? DEFAULT_ACCEPTED_FORMATS
    );
    const filteredPrompts = useMemo(() => {
      if (!config || !typeOption) return [];
      return getPromptsForDocumentType(
        config.promptConfigurations,
        typeOption.promptCategory
      );
    }, [config, typeOption]);

    const {
      ocrModel,
      setOcrModel,
      llmModel,
      setLlmModel,
      promptVersion,
      setPromptVersion,
    } = useProvisionFieldSelection(config, filteredPrompts);

    const uploadComplete = upload.status === 'complete' && !!upload.file;
    const canSubmit =
      uploadComplete &&
      !!ocrModel &&
      !!llmModel &&
      !!promptVersion &&
      !submitting;

    useEffect(() => onReadyChange(canSubmit), [canSubmit, onReadyChange]);

    useImperativeHandle(ref, () => ({
      submit: () => {
        if (!canSubmit) return;
        onSubmit({
          fileSignedId: upload.file as string,
          documentExtraction: { configurationId: ocrModel!.id },
          documentExtractionCompletion: {
            configurationId: llmModel!.id,
            promptId: promptVersion!.id,
          },
        });
      },
    }));

    if (!config) {
      return (
        <InlineNotification
          kind={configErrored ? 'error' : 'info'}
          lowContrast
          hideCloseButton
          title={
            configErrored
              ? 'Could not load provisioning options. Refresh and try again.'
              : 'Loading provisioning options…'
          }
        />
      );
    }

    // A document type the backend config doesn't list can't be provisioned — say so rather than
    // leaving Save silently, permanently disabled.
    if (!typeOption) {
      return (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title="This document type can't be provisioned right now."
        />
      );
    }

    return (
      <>
        {error && (
          <InlineNotification
            kind="error"
            lowContrast
            title="Error"
            subtitle={error}
            onCloseButtonClick={onDismissError}
          />
        )}
        <DocumentUploadField
          helperText="The document is provisioned and linked to this record in one step."
          labelText="Drag and drop a file here or click to upload"
          accept={accept}
          upload={upload}
          onAddFiles={(_evt, { addedFiles }) => {
            const file = addedFiles?.[0];
            if (file) setUpload(file);
          }}
          onDelete={() => !submitting && setUpload(undefined)}
          disabled={upload.status === 'uploading' || submitting}
        />
        <ConfigureProvisionFields
          idPrefix={idPrefix}
          extractionConfigurations={config.extractionConfigurations}
          completionConfigurations={config.completionConfigurations}
          filteredPrompts={filteredPrompts}
          ocrModel={ocrModel}
          onOcrChange={setOcrModel}
          llmModel={llmModel}
          onLlmChange={setLlmModel}
          promptVersion={promptVersion}
          onPromptChange={setPromptVersion}
          promptHelperText={typeOption?.promptVersionLabel ?? ''}
          disabled={submitting}
        />
      </>
    );
  }
);

ProvisionUploadForm.displayName = 'ProvisionUploadForm';
